import { AbstractDraw, DrawInteractionState } from "../abstract-draw";
import { Feature, LineString, MultiPolygon, Point, Polygon, Position } from "geojson";
import { SnapMode, SnapPoint, SnapPointOptions } from "../../snap";
import { ShapeGenerator } from "../../generators/shape-generator";
import { GeometryUtil } from "../../geometry";
import { distanceLabelFormat } from "../../../utils";
import { DrawFeature } from "@mapbox/mapbox-gl-draw";
import { VueMapPopup } from "../../control/VueMapPopup";
import { defineAsyncComponent } from "vue";
import {mapboxglref} from "@/js/mapboxService";

let MapInputNumberMarker = defineAsyncComponent(() => import("../../../../components/map/markers/MapInputNumber.vue"));

export class ParallelogramDrawInteractionState extends DrawInteractionState {
  enclosingOrigin: Position | undefined;
  referenceOrigin: Position | undefined;
  parallelogramOrigin: Position | undefined;
  width: number | undefined;
  height: number | undefined;
  angle: number | undefined;
}

export class EditorDrawParallelogram extends AbstractDraw<ParallelogramDrawInteractionState> {
  public mapboxgl = mapboxglref();
  private enclosingOrigin: Feature<Point> | undefined;
  private referenceOrigin: Feature<Point> | undefined;
  private parallelogramOrigin: Feature<Point> | undefined;

  private widthLabel: Feature<Point> | undefined;
  private heightLabel: Feature<Point> | undefined;

  private heightInput: VueMapPopup<typeof MapInputNumberMarker> | undefined;
  private widthInput: VueMapPopup<typeof MapInputNumberMarker> | undefined;
  private angleInput: VueMapPopup<typeof MapInputNumberMarker> | undefined;

  private drawLine: Feature<LineString> | undefined;
  private angle: DrawFeature | undefined;
  private angleLabel: Feature<Point> | undefined;


  private originParallelogram: Feature<Polygon> | undefined;
  private parallelogramMatrix: Feature<MultiPolygon> | undefined;

  getSupportedSnapModes(): SnapMode[] {
    return ['snap', 'trace', 'manual'];
  }

  buildInitialState(initialState: ParallelogramDrawInteractionState): ParallelogramDrawInteractionState {
    let state = super.buildInitialState(initialState);

    state.enclosingOrigin = initialState.enclosingOrigin ?? undefined;
    state.referenceOrigin = initialState.referenceOrigin ?? undefined;
    state.parallelogramOrigin = initialState.parallelogramOrigin ?? undefined;
    state.isDirty = initialState.isDirty ?? false;

    if (state.enclosingOrigin) {
      this._generateEnclosingOriginPoint(state);
    }
    if (state.referenceOrigin) {
      this._generateReferenceOriginPoint(state);
      //this._generateWidthLabel(state);
    }
    if (state.parallelogramOrigin) {
      this._generateParallelogramOriginPoint(state);
      //this._generateHeightLabel(state);
    }

    return state;
  }

  save(state: any, nextInteraction: string | undefined = undefined) {
    this.createLayerGeometries(
      [
        { type: 'Feature', properties: {}, geometry: this.originParallelogram.geometry },
        ...this.parallelogramMatrix.geometry.coordinates.map((pair, index) => {
          return {
            type: 'Feature',
            properties: {},
            geometry: this.createPolygon(pair[0], index.toString()).geometry
          } as Feature
        })
      ],
      nextInteraction
    )
  }

  onStop(state: any) {
    super.onStop(state);

    this.enclosingOrigin = undefined;
    this.referenceOrigin = undefined;
    this.parallelogramOrigin = undefined;
    this.drawLine = undefined;
    this.originParallelogram = undefined;
    this.parallelogramMatrix = undefined;

    if (this.widthInput) {
      this.widthInput.remove();
      this.widthInput = undefined;
    }
    if (this.heightInput) {
      this.heightInput.remove();
      this.heightInput = undefined;
    }
    if (this.angleInput) {
      this.angleInput.remove();
      this.angleInput = undefined;
    }
  }

  async onClick(state: ParallelogramDrawInteractionState, e: mapboxgl.MapMouseEvent) {
    this.snapPoint = this._getSnapPoint(state, e);

    if (!state.enclosingOrigin) {
      this.controller.interaction.historyPush(state);

      state.enclosingOrigin = this.snapPoint.point;
      this._generateEnclosingOriginPoint(state);
      this.tick();
      return;
    }
    if (!state.referenceOrigin) {
      this.controller.interaction.historyPush(state);

      state.referenceOrigin = this.snapPoint.point;
      this._generateReferenceOriginPoint(state)
      //this._generateWidthLabel(state);

      //this.removeDrawFeature(this.drawLine.id.toString());
      //this.drawLine = undefined;

      this.tick();
      return;
    }

    if (!state.parallelogramOrigin) {
      this.controller.interaction.historyPush(state);

      state.parallelogramOrigin = this.snapPoint.point;
      this._generateParallelogramOriginPoint(state);
      this._generateOriginParallelogram(state, this.snapPoint);

      //this._generateWidthLabel(state);
      //this._generateHeightLabel(state, this.snapPoint);
      this.tick();
      return;
    }

    this.save(state);
  }

  async onMouseMove(state: ParallelogramDrawInteractionState, e: mapboxgl.MapMouseEvent) {
    await super.onMouseMove(state, e);
    if (!state.enclosingOrigin) {
      return;
    }

    if (!state.referenceOrigin) {

      let drawLinePoints = [state.enclosingOrigin];

      if (state.referenceOrigin) {
        drawLinePoints.push(state.referenceOrigin);
      }

      this.drawLine = this.createLineString([...drawLinePoints, this.snapPoint.point], 'draw-line');
      this.displayDrawFeature(this.drawLine);

      //this._generateWidthLabel(state, this.snapPoint);

      return;
    }

    if(!this.originParallelogram && this.parallelogramOrigin)
      this._generateOriginParallelogram(state);

    if (!this.parallelogramOrigin) {
      this._generateOriginParallelogram(state, this.snapPoint);
      //this._generateWidthLabel(state);
      //this._generateHeightLabel(state, this.snapPoint);
      return;
    }

    this.parallelogramMatrix = this.createMultiPolygon(
      ShapeGenerator.generateParallelogramMatrix(this.originParallelogram.geometry.coordinates[0], this.snapPoint.point),
      'parallelogram_matrix'
    );
    this.displayDrawFeature(this.parallelogramMatrix);

    //this._generateWidthLabel(state);
    //this._generateHeightLabel(state);
  }

  onSnapModeChange(state: ParallelogramDrawInteractionState, mode: SnapMode) {
    if (mode !== 'manual') {
      state.width = undefined;
      state.height = undefined;
      state.angle = undefined;
    }
  }

  protected _generateEnclosingOriginPoint(state: ParallelogramDrawInteractionState): void {
    this.enclosingOrigin = this.createIcon(state.enclosingOrigin, 'map-pin', {
      id: 'enclosing_origin',
      "icon-anchor": "bottom"
    });
    this.displayDrawFeature(this.enclosingOrigin);
  }

  protected _generateReferenceOriginPoint(state: ParallelogramDrawInteractionState): void {
    this.referenceOrigin = this.createIcon(state.referenceOrigin, 'map-pin', {
      id: 'reference_origin',
      "icon-anchor": "bottom"
    });
    this.displayDrawFeature(this.referenceOrigin);
  }

  protected _generateParallelogramOriginPoint(state: ParallelogramDrawInteractionState): void {
    this.parallelogramOrigin = this.createIcon(state.parallelogramOrigin, 'map-pin', {
      id: 'parallelogram_origin',
      "icon-anchor": "bottom"
    });
    this.displayDrawFeature(this.parallelogramOrigin);
  }

  protected _generateOriginParallelogram(state: ParallelogramDrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    this.originParallelogram = this.createPolygon(
      ShapeGenerator.generateParallelogramFromThreePoints(
        state.enclosingOrigin,
        state.referenceOrigin,
        lastPoint ? lastPoint.point : state.parallelogramOrigin
      ),
      'origin_parallelogram'
    );
    this.displayDrawFeature(this.originParallelogram);
  }

  protected _generateWidthLabel(state: ParallelogramDrawInteractionState, lastPoint: SnapPoint | undefined = undefined) {
    let length = GeometryUtil.pointsDistance(state.enclosingOrigin, lastPoint ? lastPoint.point : state.referenceOrigin);
    let midpoint = GeometryUtil.midpoint(state.enclosingOrigin, lastPoint ? lastPoint.point : state.referenceOrigin);

    if (state.referenceOrigin === undefined && this.controller.snapUtility.getMode() === 'manual') {
      if (this.widthLabel) {
        this.removeDrawFeature(this.widthLabel.id.toString());
        this.widthLabel = undefined;
      }
      if (!this.widthInput) {
        this.widthInput = new VueMapPopup<typeof MapInputNumberMarker>(MapInputNumberMarker, {
          initialValue: Math.round(length),
          min: 0,
          styleclass: 'map-input-number-lg',
          suffix: ' m',
          onInput: (value: number) => state.width = value
        });
        this.widthInput
        .setLngLat(new this.mapboxgl.LngLat(midpoint[0], midpoint[1]))
        .addTo(this.controller.mapInstance);
      }

      this.widthInput
      .setLngLat(new this.mapboxgl.LngLat(midpoint[0], midpoint[1]))
      .ctx()?.setValue(Math.round(length));
    } else {
      if (this.widthInput) {
        this.widthInput.remove();
        this.widthInput = undefined;
      }
      //this.widthLabel = this.createLabel(midpoint, distanceLabelFormat(length), { id: 'width_label' });
      //this.displayDrawFeature(this.widthLabel);
    }
  }

  protected _generateHeightLabel(state: ParallelogramDrawInteractionState, lastPoint: SnapPoint | undefined = undefined) {
    let length = GeometryUtil.pointsDistance(state.referenceOrigin, lastPoint ? lastPoint.point : state.parallelogramOrigin);
    let midpoint = GeometryUtil.midpoint(state.referenceOrigin, lastPoint ? lastPoint.point : state.parallelogramOrigin);
    let angleMeasurement = ShapeGenerator.generateAngleMeasurement(
      state.enclosingOrigin,
      state.referenceOrigin,
      lastPoint ? lastPoint.point : state.parallelogramOrigin
    );

    if (!state.parallelogramOrigin && this.controller.snapUtility.getMode() === 'manual') {
      /*if (this.heightLabel) {
        this.removeDrawFeature(this.heightLabel.id.toString());
        this.heightLabel = undefined;
      }
      if (this.angleLabel) {
        this.removeDrawFeature(this.angleLabel.id.toString());
        this.angleLabel = undefined;
      }
      if (this.angle) {
        this.removeDrawFeature(this.angle.id.toString());
        this.angle = undefined;
      }*/
      if (!this.heightInput) {
        this.heightInput = new VueMapPopup<typeof MapInputNumberMarker>(MapInputNumberMarker, {
          initialValue: length,
          min: 1,
          styleclass: 'map-input-number-lg',
          suffix: ' m',

          onInput: (value: number) => state.height = value
        })
        this.heightInput.addTo(this.controller.mapInstance);
      }

      this.heightInput
      .setLngLat(new this.mapboxgl.LngLat(midpoint[0], midpoint[1]))
      .ctx()?.setValue(Math.round(length));

      if (!this.angleInput) {
        this.angleInput = new VueMapPopup<typeof MapInputNumberMarker>(MapInputNumberMarker, {
          initialValue: angleMeasurement.properties.label,
          suffix: '°',
          min: 0,
          max: 359,
          onInput: (value: number) => state.angle = value
        });

        this.angleInput
        .setLngLat(new this.mapboxgl.LngLat(state.referenceOrigin[0], state.referenceOrigin[1]))
        .addTo(this.controller.mapInstance);
      }
      this.angleInput
      .setLngLat(new this.mapboxgl.LngLat(state.referenceOrigin[0], state.referenceOrigin[1]))
      .ctx()?.setValue(angleMeasurement.properties.label);
    } else {
      if (this.angleInput) {
        this.angleInput.remove();
        this.angleInput = undefined;
      }
      if (this.heightInput) {
        this.heightInput.remove();
        this.heightInput = undefined;
      }
      //this.heightLabel = this.createLabel(midpoint, distanceLabelFormat(length), { id: 'height_label' });
      //this.displayDrawFeature(this.heightLabel);

      //this.angle = this.createFeature({ ...angleMeasurement, id: 'angle' });
      //this.displayDrawFeature(this.angle);

      //this.angleLabel = this.createLabel(this.angle.properties.labelPosition, `${this.angle.properties.label} °`, { id: 'angle_label' });
      //this.displayDrawFeature(this.angleLabel);
    }
  }

  protected _getSnapPoint(state: ParallelogramDrawInteractionState, e: mapboxgl.MapMouseEvent): SnapPoint {
    let opts: Partial<SnapPointOptions> = {};
    if (state.referenceOrigin === undefined && state.width) {
      opts.width = state.width;
    }
    if (state.parallelogramOrigin === undefined && state.height) {
      opts.width = state.height;
    }
    if (state.parallelogramOrigin === undefined && state.angle) {
      opts.angle = state.angle;
    }

    if (this.enclosingOrigin && this.referenceOrigin && !this.parallelogramOrigin) {
      return this.controller.snapUtility.getSnapPoint(
        e,
        [],
        this.referenceOrigin.geometry.coordinates,
        this.enclosingOrigin.geometry.coordinates,
        opts
      );
    }

    if (this.enclosingOrigin && !this.parallelogramOrigin) {
      return this.controller.snapUtility.getSnapPoint(e, [], this.enclosingOrigin.geometry.coordinates, undefined, opts)
    }

    return this.controller.snapUtility.getSnapPoint(e, [], undefined, undefined, opts);
  }
}
